import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import qs from 'query-string'
import { useLocation } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'
import { get } from 'lodash'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import Skeleton from '@material-ui/lab/Skeleton'
import Error from 'components/error'
import MarketingOptIn from 'components/marketing-opt-in/marketing-opt-in'
import RouterLinkButton from 'components/router-link-button'
import { CoachingCenterWaiver } from 'components/waivers'
import { currency } from 'lib/utils/string'
import { getPriceLabel } from 'pages/schedule/SelectLessonTime/redesign/booking-summary-card'
import CancellationCallout from 'pages/schedule/components/cancellation-callout'
import StripePaymentElementsForm from '../stripe-payment-elements-form'
import useLessonReviewCheckout from '../use-lesson-review-checkout'
import CheckoutCard from './checkout-card'
import ReviewFrame from './review-frame'
import ReviewSummaryCard from './review-summary-card'
import { STRIPE_APPEARANCE } from './stripe-appearance'
import StudentRow from './student-row'
import useStyles from './styles'
import {
  buildChangeStudentUrl,
  buildChangeTimeUrl,
  formatLessonLine,
  formatSlotRange,
  getStudentSubtitle,
} from './utils'
import YourTimeCard from './your-time-card'

const getBarLabel = ({ showTotal, totalLabel }) => {
  if (!showTotal) return null
  return totalLabel ? `Total · ${totalLabel}` : 'Step 3 of 3'
}

const PAY_AT_LOCATION = 'Pay at location'
const ENCRYPTED_NOTE = 'Payments are encrypted. Card details are never stored.'

// Redesigned review + payment step of private lesson booking, shown when
// FLAG_FEAT_PRIVATE_BOOKING_REDESIGN is on. The legacy layout lives in
// ../lesson-review-layout.js; both render from `useLessonReviewCheckout`, so
// payment intent creation, skip payment, waiver, analytics and the success
// paths are the same code.
const LessonReviewRedesign = ({
  lessonType,
  locationId,
  startDateTime,
  student,
  previousUrl,
  isComplete,
  setIsComplete,
  stepText,
  eventObject = {},
  guest = null,
}) => {
  const classes = useStyles()
  const location = useLocation()
  const { student: studentParam } = qs.parse(location.search)
  const { priceInCents, coach, acceptsOnlinePayments } = lessonType
  const priceInDollars = priceInCents ? priceInCents / 100 : null

  const {
    idOrSlug,
    user,
    facility,
    canSkipPayment,
    coachBooksOnBehalfOfStudent,
    shouldShowWaiver,
    showWaiver,
    setShowWaiver,
    paymentRequired,
    setPaymentRequired,
    userIsNotOptedIn,
    marketingOptInChecked,
    setMarketingOptInChecked,
    stripePaymentFormRef,
    clientSecret,
    clientSecretLoading,
    clientSecretError,
    prepareLessonSuccess,
    prepareLessonMessage,
    bookNewSessionUrl,
    pendingButtonAction,
    form,
    handleButtonClick,
    handleWaiverSubmit,
  } = useLessonReviewCheckout({
    lessonType,
    locationId,
    startDateTime,
    student,
    isComplete,
    setIsComplete,
    eventObject,
    guest,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const mounted = useRef(true)
  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )

  const timezone =
    facility?.timezone || get(lessonType, 'facilities.0.timezone')
  const slotLabel = formatSlotRange(
    startDateTime,
    lessonType.durationInMinutes,
    timezone,
  )
  const studentName = `${student.firstName} ${student.lastName}`
  const urlParts = {
    idOrSlug,
    lessonTypeId: lessonType.id,
    locationId,
    startDateTime,
  }
  const changeTimeUrl = buildChangeTimeUrl({
    ...urlParts,
    studentParam,
    timezone,
  })
  const changeStudentUrl = buildChangeStudentUrl(urlParts)

  const needsPaymentForm = !!paymentRequired && !!acceptsOnlinePayments
  const busy =
    isSubmitting ||
    !!pendingButtonAction ||
    (needsPaymentForm && clientSecretLoading && !clientSecret)
  // Same enabled condition as the legacy step's button (only disabled while its
  // form is submitting). While busy the label still shows progress and extra
  // clicks are ignored in `handleSubmit`, but the button stays enabled.
  const payDisabled = form.formState.isSubmitting

  // The card sits in a hidden wrapper while the waiver shows, so close the
  // waiver on a payment error to put the inline alert in front of the golfer.
  const handlePaymentError = message => {
    setPaymentError(message)
    setShowWaiver(false)
  }

  const handleSubmit = async event => {
    event.preventDefault()
    if (busy || (needsPaymentForm && !clientSecret)) return
    setPaymentError(null)
    setIsSubmitting(true)
    try {
      await handleButtonClick()
    } finally {
      if (mounted.current) setIsSubmitting(false)
    }
  }

  if (clientSecretError) return <Error error={clientSecretError} />

  // Handle prepareLessonForStudent errors
  if (!clientSecretLoading && prepareLessonSuccess === false) {
    return (
      <ReviewFrame
        lessonType={lessonType}
        facility={facility}
        coach={coach}
        previousUrl={previousUrl}
        stepText={stepText}
        summaryCard={
          <ReviewSummaryCard
            lessonType={lessonType}
            facility={facility}
            coach={coach}
            slotLabel={slotLabel}
            totalLabel={getPriceLabel(lessonType)}
          />
        }
      >
        <div className={classes.errorState}>
          <RouterLinkButton
            variant="outlined"
            color="primary"
            to={bookNewSessionUrl}
            data-testid="retry-booking-button"
          >
            Retry Booking
          </RouterLinkButton>
          <Error message={prepareLessonMessage || 'An error occurred.'} />
        </div>
      </ReviewFrame>
    )
  }

  const payLabel = (() => {
    if (isSubmitting || pendingButtonAction) return 'Processing…'
    if (shouldShowWaiver) return 'Continue'
    if (!acceptsOnlinePayments) return 'Complete booking'
    if (paymentRequired) {
      return priceInDollars !== null
        ? `Pay ${currency(priceInDollars)} and book`
        : 'Complete purchase'
    }
    return 'Complete booking'
  })()

  const totalLabel = getPriceLabel(lessonType)
  const lineItems =
    priceInDollars !== null
      ? [
        {
          label: formatLessonLine(lessonType),
          amount: currency(priceInDollars),
        },
      ]
      : []
  const due =
    priceInDollars !== null
      ? {
        label: 'Due today',
        amount: currency(paymentRequired ? priceInDollars : 0),
      }
      : null

  const showSkeleton = needsPaymentForm && !clientSecret && clientSecretLoading

  return (
    <>
      <div
        className={clsx(classes.waiver, showWaiver && classes.waiverVisible)}
      >
        <CoachingCenterWaiver
          form={form}
          showWaiver={showWaiver}
          setShowWaiver={setShowWaiver}
          onSubmit={handleWaiverSubmit}
          withBackButton={true}
        />
      </div>
      <div style={{ display: showWaiver ? 'none' : 'block' }}>
        <ReviewFrame
          lessonType={lessonType}
          facility={facility}
          coach={coach}
          previousUrl={previousUrl}
          stepText={stepText}
          summaryCard={
            <ReviewSummaryCard
              lessonType={lessonType}
              facility={facility}
              coach={coach}
              slotLabel={slotLabel}
              totalLabel={totalLabel}
              totalNote={
                !paymentRequired && priceInDollars !== null
                  ? PAY_AT_LOCATION
                  : null
              }
            />
          }
        >
          {({ isCompact }) => (
            <>
              <YourTimeCard slotLabel={slotLabel} editUrl={changeTimeUrl} />
              <FormProvider {...form}>
                <CheckoutCard
                  isCompact={isCompact}
                  onSubmit={handleSubmit}
                  studentRow={
                    <StudentRow
                      name={studentName}
                      subtitle={getStudentSubtitle({
                        student,
                        guest,
                        user,
                        coachBooksOnBehalfOfStudent,
                      })}
                      editUrl={changeStudentUrl}
                    />
                  }
                  heading={paymentRequired ? 'Payment' : 'Confirm your booking'}
                  lineItems={lineItems}
                  due={due}
                  dueNote={
                    !paymentRequired && priceInDollars !== null
                      ? PAY_AT_LOCATION
                      : null
                  }
                  optIn={
                    userIsNotOptedIn && !coachBooksOnBehalfOfStudent ? (
                      <MarketingOptIn
                        checked={marketingOptInChecked}
                        onChange={setMarketingOptInChecked}
                        useMarketingOptIn={'callout'}
                      />
                    ) : null
                  }
                  callout={
                    isCompact ? (
                      <CancellationCallout
                        canStudentCancel={coach?.canStudentCancel}
                        cancellationPolicy={coach?.cancellationPolicy}
                      />
                    ) : null
                  }
                  error={paymentError}
                  button={{
                    label: payLabel,
                    disabled: payDisabled,
                    busy,
                  }}
                  caption={needsPaymentForm ? ENCRYPTED_NOTE : null}
                  barLabel={getBarLabel({
                    // The pay button already carries the amount.
                    showTotal: !(needsPaymentForm && priceInDollars !== null),
                    totalLabel,
                  })}
                >
                  {canSkipPayment && acceptsOnlinePayments && (
                    <div className={classes.skipRow}>
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={!paymentRequired}
                            onChange={() => {
                              setPaymentError(null)
                              setPaymentRequired(!paymentRequired)
                            }}
                          />
                        }
                        label="Skip payment"
                      />
                    </div>
                  )}
                  {showSkeleton && (
                    <div
                      className={classes.stripeSkeleton}
                      data-testid="payment-skeleton"
                    >
                      <Skeleton
                        variant="rect"
                        height={52}
                        style={{ borderRadius: 12, marginBottom: 12 }}
                      />
                      <Skeleton
                        variant="rect"
                        height={52}
                        style={{ borderRadius: 12 }}
                      />
                    </div>
                  )}
                  <div className={classes.stripeWrap}>
                    <StripePaymentElementsForm
                      ref={stripePaymentFormRef}
                      clientSecret={clientSecret}
                      secretLoading={false}
                      coachSlug={idOrSlug}
                      studentId={student.id}
                      marketingOptInChecked={
                        userIsNotOptedIn ? marketingOptInChecked : null
                      }
                      eventObject={eventObject}
                      paymentRequired={paymentRequired}
                      setPaymentRequired={setPaymentRequired}
                      lessonType={lessonType}
                      showHeading={false}
                      appearance={STRIPE_APPEARANCE}
                      onSubmitError={handlePaymentError}
                    />
                  </div>
                </CheckoutCard>
              </FormProvider>
            </>
          )}
        </ReviewFrame>
      </div>
    </>
  )
}

LessonReviewRedesign.propTypes = {
  lessonType: PropTypes.object.isRequired,
  locationId: PropTypes.string.isRequired,
  startDateTime: PropTypes.string.isRequired,
  student: PropTypes.object.isRequired,
  guest: PropTypes.shape({
    contact: PropTypes.object,
    student: PropTypes.object,
  }),
  previousUrl: PropTypes.string,
  isComplete: PropTypes.bool,
  setIsComplete: PropTypes.func,
  stepText: PropTypes.string,
  eventObject: PropTypes.object,
}

LessonReviewRedesign.defaultProps = {
  isComplete: false,
}

export default LessonReviewRedesign
